import {
  buildElementTimingMap,
  extractTimingsFromEditorElements,
  resolveSceneRange,
} from '@src/utility/sceneAnchors';

import type { Scene } from '@src/editor/models/layout/Scene';
import type { EditorElement } from '@src/utility/sceneAnchors';

/**
 * How far into a scene a click lands, rather than exactly on its first frame.
 *
 * The boundary computed here (`sceneAnchors.resolveSceneRange`, over
 * `durationSeconds`) and the one the layout index computes (`AnchorResolver`,
 * over `duration`, rounded on its way through the source JSON) can differ by a
 * fraction of a millisecond — more than the index's `TIME_EPSILON` (0.0001)
 * forgives. Seeking to the boundary itself can therefore still resolve as the
 * last frame of the *previous* scene, and the reframe modal then re-derives
 * that scene's framing instead of this one's (PLAT-11236).
 *
 * Well under half a frame at 30fps, so the frame shown is the scene's first
 * either way.
 */
const SEEK_INTO_SCENE_SECONDS = 0.01;

export interface SceneBlock {
  id: string;
  /** 1-based position in the clip, as shown to a creator. */
  number: number;
  start: number;
  end: number;
  /** Never the boundary itself — see `SEEK_INTO_SCENE_SECONDS`. */
  seekTo: number;
  leftPct: number;
  widthPct: number;
  /**
   * How a creator is told which scene this is: its position in the clip, and
   * nothing else. The framing template's name used to be appended here
   * ("Scene 7 · Cover"), but it is an internal id — the same word on most
   * scenes, naming something the player gives no way to change (PLAT-11504).
   */
  label: string;
  ariaLabel: string;
}

/**
 * Resolves scenes to positioned, fit-to-width blocks over a clip's duration.
 *
 * Shared so that the strip that draws the blocks and the control bar that
 * resolves which scene the cursor is over agree on every boundary — one
 * computation, not two that can drift.
 */
export function buildSceneBlocks(
  scenes: readonly Scene[],
  elements: readonly EditorElement[],
  duration: number,
): SceneBlock[] {
  if (duration <= 0) return [];

  // Once for the strip, not once per block. Filtering by type first keeps the
  // map small; extra non-anchored elements in it would be harmless (lookups
  // are by the anchor's own element id) but pointless.
  const elementTimingMap = buildElementTimingMap(
    extractTimingsFromEditorElements(
      elements.slice(),
      el => el.type === 'video' || el.type === 'composition',
    ),
  );

  const total = scenes.length;

  return scenes.map((scene, index) => {
    const { start, end } = resolveSceneRange(scene, elementTimingMap, duration);
    const number = index + 1;
    return {
      id: scene.id,
      number,
      start,
      end,
      // Capped at the block's midpoint so a scene shorter than the nudge can't
      // be stepped straight over.
      seekTo: start + Math.min(SEEK_INTO_SCENE_SECONDS, (end - start) / 2 || 0),
      leftPct: (start / duration) * 100,
      widthPct: (Math.max(end - start, 0) / duration) * 100,
      label: `Scene ${number} of ${total}`,
      ariaLabel: `Jump to scene ${number} of ${total}`,
    };
  });
}

/** The block containing `time`, or `undefined` outside every block. */
export function findSceneBlockAt(
  blocks: readonly SceneBlock[],
  time: number,
): SceneBlock | undefined {
  return blocks.find(block => time >= block.start && time < block.end);
}

/**
 * A stretch of the clip that belongs to a card rather than to a scene.
 *
 * Scenes anchor to the original video element (`sceneAnchors.resolveSceneRange`),
 * so an intro card ahead of the video — or an end card after it — sits inside
 * no scene. Passed in by the host, which is the layer that can read
 * `extraElementData` and classify a composition.
 */
export interface ClipCardSpan {
  id: string;
  start: number;
  end: number;
  /** What a creator is told the stretch is, e.g. "Intro Card". */
  label: string;
}

/** A stretch of the rail: one scene, or one card between scenes. */
export interface RailBlock {
  id: string;
  kind: 'scene' | 'card';
  start: number;
  end: number;
  leftPct: number;
  widthPct: number;
  /** Named in the hover card. Undefined where there is nothing to name. */
  label?: string;
}

/**
 * Narrowest gap worth drawing, as a share of the rail. Two boundaries resolved
 * from different anchors can differ by a fraction of a millisecond, and a block
 * that thin is a sub-pixel sliver at any player width — a seam's worth of
 * background, not a stretch of the clip.
 */
const MIN_GAP_PCT = 0.1;

/** The card overlapping `[start, end)` most, or `undefined` if none does. */
function bestCardFor(
  cards: readonly ClipCardSpan[],
  start: number,
  end: number,
): ClipCardSpan | undefined {
  let best: ClipCardSpan | undefined;
  let bestOverlap = 0;
  for (const card of cards) {
    const overlap = Math.min(end, card.end) - Math.max(start, card.start);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = card;
    }
  }
  return best;
}

function pushGapBlock(
  out: RailBlock[],
  start: number,
  end: number,
  duration: number,
  cards: readonly ClipCardSpan[],
): void {
  const widthPct = ((end - start) / duration) * 100;
  if (widthPct < MIN_GAP_PCT) return;
  const card = bestCardFor(cards, start, end);
  out.push({
    id: card ? `card-${card.id}` : `gap-${start.toFixed(3)}`,
    kind: 'card',
    start,
    end,
    leftPct: (start / duration) * 100,
    widthPct,
    label: card?.label,
  });
}

/**
 * Where the clip's content actually runs, measured from the video elements the
 * scenes are anchored to.
 *
 * A scene's end anchor can name the clip's bound rather than its content —
 * `clip-end`, or the end card itself once it is the last element on the track.
 * Its resolved range then runs past the video and over the card that is really
 * on screen there, so the card never reaches the rail. Clamping every scene to
 * this span decides that by what is playing instead of by which anchor a scene
 * happens to carry.
 *
 * It also settles the overlap case: a card given an `overlapBefore` /
 * `overlapAfter` fades in while the video is still playing under it, and that
 * stretch stays inside the content span — so the scene keeps it, and the card
 * takes only the part it has to itself.
 *
 * `undefined` when there is no video element to measure, which leaves the
 * scenes taken at their word.
 */
function contentSpan(
  elements: readonly EditorElement[],
): { start: number; end: number } | undefined {
  let start = Infinity;
  let end = -Infinity;
  for (const element of elements) {
    if (element.type !== 'video') continue;
    start = Math.min(start, element.time);
    end = Math.max(end, element.time + element.durationSeconds);
  }
  return end > start ? { start, end } : undefined;
}

/**
 * Every stretch of the clip, in order, with nothing left uncovered.
 *
 * `buildSceneBlocks` describes only the scenes, so the rail it drives goes
 * blank wherever a card holds the screen — no bar, no progress fill, and no
 * hover card, over a stretch that is still seekable. This fills those stretches
 * in, naming them from `cards` where one matches, so the rail reads as one
 * continuous clip and every part of it answers a hover.
 */
export function buildRailBlocks(
  scenes: readonly Scene[],
  elements: readonly EditorElement[],
  duration: number,
  cards: readonly ClipCardSpan[] = [],
): RailBlock[] {
  const sceneBlocks = buildSceneBlocks(scenes, elements, duration);
  if (sceneBlocks.length === 0) return [];

  const content = contentSpan(elements);
  const blocks: RailBlock[] = [];
  let cursor = 0;

  for (const block of sceneBlocks) {
    const start = content ? Math.max(block.start, content.start) : block.start;
    const end = content ? Math.min(block.end, content.end) : block.end;
    // A scene lying wholly outside the content span is describing card time,
    // which the card itself is about to claim.
    if (end <= start) continue;

    pushGapBlock(blocks, cursor, start, duration, cards);
    blocks.push({
      id: block.id,
      kind: 'scene',
      start,
      end,
      leftPct: (start / duration) * 100,
      widthPct: ((end - start) / duration) * 100,
      label: block.label,
    });
    // `max` rather than assignment: scenes are authored in order, but an
    // overlapping pair would otherwise walk the cursor backwards and emit a
    // negative-width gap.
    cursor = Math.max(cursor, end);
  }

  pushGapBlock(blocks, cursor, duration, duration, cards);

  return blocks;
}
